/**
 * <s-timeline>: scroll-driven reveal for a vertical timeline.
 * - Grows the line ([data-timeline-line]) as the timeline scrolls through the viewport via --tl-progress.
 * - Reveals each [data-timeline-item] once it enters the viewport by setting data-visible.
 * Without JS, or with reduced motion, everything stays fully visible.
 */
class STimeline extends HTMLElement {
  #observer = null;
  #frame = 0;
  #onScroll = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#updateProgress();
    });
  };

  connectedCallback() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    // Keep every step visible and selectable while editing in Canvas or the theme editor.
    if (window.Shopify?.designMode) return;
    if (!('IntersectionObserver' in window)) return;

    const items = this.querySelectorAll('[data-timeline-item]');
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.setAttribute('data-visible', '');
            this.#observer?.unobserve(entry.target);
          }
        }
      },
      { rootMargin: '0px 0px -15% 0px', threshold: 0.15 },
    );
    items.forEach((item) => this.#observer?.observe(item));

    this.setAttribute('data-ready', '');
    window.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onScroll, { passive: true });
    this.#updateProgress();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    window.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onScroll);
    if (this.#frame) cancelAnimationFrame(this.#frame);
  }

  #updateProgress() {
    const rect = this.getBoundingClientRect();
    const viewport = window.innerHeight || 1;
    const start = viewport * 0.8;
    const travelled = start - rect.top;
    const progress = Math.min(1, Math.max(0, travelled / Math.max(rect.height, 1)));
    this.style.setProperty('--tl-progress', progress.toFixed(3));
  }
}

if (!customElements.get('s-timeline')) customElements.define('s-timeline', STimeline);
